import { chunk } from "lodash";

import { clamp } from "./math";

/**
 * Clean a hex color code, to return a 6-digit hex code without the '#'
 * @param hexStr The hex string to clean
 */
const cleanHex = (hexStr: string): string => {
	let output = hexStr.replace(/#/, "").trim();
	if (output.length === 3) {
		output = output.replace(/(.)/g, "$1$1");
	}
	return output;
};

/**
 * A function for interpolating colors. Adapted from https://gist.github.com/maxwells/8251275
 * @param left The color you start with. A percentage of 0 will result in this color.
 * @param right The color you end with. A percentage of 100 will result in this color.
 * @param percentage The amount to move between the colors.
 * @returns A new, opaque color as a hex code with out the '#' (to be consistent with UI_HEX_COLORS).
 */
const findColorBetween = (left: string, right: string, percentage: number) => {
	const leftHex = cleanHex(left);
	const rightHex = cleanHex(right);

	const interpolate = (leftValue: string, rightValue: string) => {
		const leftDecimal = parseInt(leftValue, 16);
		const rightDecimal = parseInt(rightValue, 16);

		const mixedValue = Math.round(leftDecimal + (rightDecimal - leftDecimal) * (percentage / 100));
		return mixedValue.toString(16);
	};

	const r = interpolate(leftHex.slice(0, 2), rightHex.slice(0, 2));
	const g = interpolate(leftHex.slice(2, 4), rightHex.slice(2, 4));
	const b = interpolate(leftHex.slice(4, 6), rightHex.slice(4, 6));

	return `${r}${g}${b}`;
};

/**
 * A function for converting css rgb functions to hex codes.
 * Needed now since we switched to using rgb values for the base colors in variables.css
 * @param rgb A color in css's rgb format. i.e. "rgb(255, 255, 255)"
 * @returns The same colour as a hex code, without the '#' (to be consistent with UI_HEX_COLORS).
 */
const rgbToHex = (rgb: string) => {
	// Strip away the spaces
	const noSpaces = rgb.replace(" ", "");

	// Remove css function
	const valuesString = noSpaces.split("(")[1].split(")")[0];

	// Separate by colour value and convert to base16
	const base16Values = valuesString.split(",").map(v => parseInt(v).toString(16));

	// Glue it back together
	return base16Values.join("");
};

export function normalizeHue(hue: number) {
	return ((hue % 360) + 360) % 360;
}

function srgbToLinear(channel: number) {
	const boundedChannel = clamp(channel, 0, 1);
	if (boundedChannel <= 0.04045) {
		return boundedChannel / 12.92;
	}
	return Math.pow((boundedChannel + 0.055) / 1.055, 2.4);
}

function linearToSrgb(channel: number) {
	const boundedChannel = clamp(channel, 0, 1);
	if (boundedChannel <= 0.0031308) {
		return 12.92 * boundedChannel;
	}
	return 1.055 * Math.pow(boundedChannel, 1 / 2.4) - 0.055;
}

function channelToHex(channel: number) {
	const value = Math.round(clamp(channel, 0, 1) * 255);
	return value.toString(16).padStart(2, "0");
}

export const getOpaqueFadedColor = (
	color: string,
	layer: "second" | "base" = "second",
	layerPercentageDark = 60,
	layerPercentageLight = 80,
) => {
	const layerVariable = layer === "base" ? "--baseLayerColor" : "--secondLayerColor";
	const bkgColor = rgbToHex(getComputedStyle(document.documentElement).getPropertyValue(layerVariable));

	if (document.documentElement.dataset.theme === "theme-dark") {
		return findColorBetween(color, bkgColor, layerPercentageDark);
	}

	return findColorBetween(color, bkgColor, layerPercentageLight);
};

export function hexToRGB(input: string) {
	return chunk(input.startsWith("#") ? input.substring(1) : input, 2)
		.map(seg => parseInt(seg.join(""), 16).toString())
		.join(",");
}

/**
 * Converts a hex color to HSL
 * @param color A color in hex format
 * @returns color.hue: number between 0 and 360
 * @returns color.saturation: number between 0 and 100
 * @returns color.lightness: number between 0 and 100
 */
export function hexToHSL(color: string) {
	// Math based on this article: https://www.niwa.nu/2013/05/math-behind-colorspace-conversions-rgb-hsl/
	const rgb = hexToRGB(color);
	const [r, g, b] = rgb.split(",").map(v => parseInt(v, 10) / 255);
	const max = Math.max(r, g, b);
	const min = Math.min(r, g, b);
	let lightness = (max + min) / 2;
	let hue = 0;
	let saturation = 0;

	if (max !== min) {
		if (lightness <= 0.5) saturation = (max - min) / (max + min);
		else saturation = (max - min) / (2 - max - min);

		if (max === r) hue = (g - b) / (max - min);
		else if (max === g) hue = 2 + (b - r) / (max - min);
		else hue = 4 + (r - g) / (max - min);

		hue *= 60;
		if (hue < 0) hue += 360;
	}

	saturation *= 100;
	lightness *= 100;

	return { hue, saturation, lightness };
}

export type OklchColor = {
	lightness: number;
	chroma: number;
	hue: number;
};

/**
 * Converts an OKLCH color to hex.
 * @param lightness OKLCH lightness (typically 0..1)
 * @param chroma OKLCH chroma
 * @param hueInDegrees OKLCH hue in degrees
 */
export function oklchToHex(lightness: number, chroma: number, hueInDegrees: number) {
	const hueInRadians = (normalizeHue(hueInDegrees) * Math.PI) / 180;
	const a = chroma * Math.cos(hueInRadians);
	const b = chroma * Math.sin(hueInRadians);

	const lPrime = lightness + 0.3963377774 * a + 0.2158037573 * b;
	const mPrime = lightness - 0.1055613458 * a - 0.0638541728 * b;
	const sPrime = lightness - 0.0894841775 * a - 1.291485548 * b;

	const l = lPrime ** 3;
	const m = mPrime ** 3;
	const s = sPrime ** 3;

	const linearRed = 4.0767416621 * l - 3.3077115913 * m + 0.2309699292 * s;
	const linearGreen = -1.2684380046 * l + 2.6097574011 * m - 0.3413193965 * s;
	const linearBlue = -0.0041960863 * l - 0.7034186147 * m + 1.707614701 * s;

	return `#${channelToHex(linearToSrgb(linearRed))}${channelToHex(linearToSrgb(linearGreen))}${channelToHex(
		linearToSrgb(linearBlue),
	)}`;
}

/**
 * Converts a hex color to OKLCH.
 * @param color A color in hex format
 * @returns lightness/chroma/hue where lightness is approximately 0..1 and hue is degrees
 */
export function hexToOklch(color: string): OklchColor {
	const normalizedHex = cleanHex(color);
	const red = parseInt(normalizedHex.slice(0, 2), 16) / 255;
	const green = parseInt(normalizedHex.slice(2, 4), 16) / 255;
	const blue = parseInt(normalizedHex.slice(4, 6), 16) / 255;

	const linearRed = srgbToLinear(red);
	const linearGreen = srgbToLinear(green);
	const linearBlue = srgbToLinear(blue);

	const l = 0.4122214708 * linearRed + 0.5363325363 * linearGreen + 0.0514459929 * linearBlue;
	const m = 0.2119034982 * linearRed + 0.6806995451 * linearGreen + 0.1073969566 * linearBlue;
	const s = 0.0883024619 * linearRed + 0.2817188376 * linearGreen + 0.6299787005 * linearBlue;

	const lRoot = Math.cbrt(l);
	const mRoot = Math.cbrt(m);
	const sRoot = Math.cbrt(s);

	const lightness = 0.2104542553 * lRoot + 0.793617785 * mRoot - 0.0040720468 * sRoot;
	const a = 1.9779984951 * lRoot - 2.428592205 * mRoot + 0.4505937099 * sRoot;
	const b = 0.0259040371 * lRoot + 0.7827717662 * mRoot - 0.808675766 * sRoot;
	const chroma = Math.sqrt(a ** 2 + b ** 2);
	const hue = normalizeHue((Math.atan2(b, a) * 180) / Math.PI);

	return { lightness, chroma, hue };
}

export function mixWithWhite(input: string, selfWeight: number) {
	const w1 = selfWeight;
	const w2 = 1 - w1;
	return `#${chunk(input.startsWith("#") ? input.substring(1) : input, 2)
		.map(seg => Math.round(parseInt(seg.join(""), 16) * w1 + 255 * w2).toString(16))
		.join("")}`;
}

export function opacity(input: string, opacity: number) {
	return input + Math.round(0xff * opacity).toString(16);
}

export function isLightColor(color: string) {
	const { lightness } = hexToHSL(color);
	return lightness > 70;
}
